<template>
  <div class="workspace-source">
    <div class="icon">
      <img v-if="typeInfoRef" :src="typeInfoRef.icon" :alt="typeInfoRef.product"/>
      <FileIcon v-else size="40"/>
    </div>
    <div class="name">{{ fileNameRef ?? 'Unknown file' }}</div>
    <div class="desc">{{ descriptionRef }}</div>
    <div v-if="folderRef" class="location">{{ folderRef }}</div>
  </div>
</template>

<style lang="scss" scoped>
.workspace-source {
  display: grid;
  grid-template-columns: max-content 1fr;
  column-gap: 0.75rem;
  align-items: center;

  .icon {
    grid-row: 1 / span 3;
    display: flex;
    align-items: center;

    img {
      width: 48px;
    }
  }

  .name {
    font-weight: 500;
  }

  .desc,
  .location {
    font-size: 0.75rem;
    color: #aaa;
    overflow-wrap: anywhere;
  }
}
</style>

<script setup lang="ts">
import { computed } from 'vue'
import { FileIcon } from '@zhuowenli/vue-feather-icons'
import { useWorkspaceStore } from '@/rd/WorkspaceStore'
import { LoadMode } from '@webapp/util/AsyncData'
import { FileUtil } from '@webapp/util/FileUtil'
import { osDisplayName } from '@webapp/util/DeviceUtil'

const props = defineProps<{
  workspaceId: string
}>()

const workspaceStore = useWorkspaceStore()

const workspaceRef = computed(() => workspaceStore.getAsyncWorkspace(props.workspaceId, LoadMode.EnsureLoaded)?.data)
const typeInfoRef = computed(() => workspaceRef.value?.workspaceTypeInfo)
// The workspace's dataFilePath is the authoritative source-file location (client-maintained). It is
// never derived from the last sync -- an absent value means the source location is genuinely unknown.
const filePathRef = computed(() => workspaceRef.value?.dataFilePath)
const fileNameRef = computed(() => {
  const name = FileUtil.fileName(filePathRef.value)
  return name ? FileUtil.stripExtension(name) : undefined
})
const folderRef = computed(() => FileUtil.folder(filePathRef.value))
const descriptionRef = computed(() => {
  const fileTypeDesc = typeInfoRef.value?.description ?? 'Unknown file'
  const device = workspaceRef.value?.clientDevice
  const os = osDisplayName(device?.os)
  if (!device || !os) {
    return fileTypeDesc
  }
  return device.name ? `${fileTypeDesc} on ${os} (${device.name})` : `${fileTypeDesc} on ${os}`
})
</script>
